import {
  useMemo,
  useState,
  Dispatch,
  ReactNode,
  useContext,
  createContext,
  SetStateAction,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchCustomer,
  updateSubscription as updateSubscriptionService,
} from '@account_module/services';
import { useSubscriptions } from '@account_module/hooks/useSubscriptions';
import { SUBSCRIPTION_QUERY_KEY } from '@account_module/constants/subscriptionKeys';
import { useUser } from '@auth_module/hooks/useUser';
import {
  ICustomer,
  ActiveSubscriptionType,
  ICustomerSubscription,
  ISubscription,
} from '@app/types/Billing';
import { unimplemented } from '@utils/commonUtils';
import { CommonResponse } from '@auth_module/services';
import { getDifferenceInDays } from '@utils/dateUtils';

type ContextValueType = {
  customer: ICustomer | null;
  activeSubscription: ActiveSubscriptionType | null;
  latestSubscription: ICustomerSubscription | null;
  remainingDaysUntilPayment: number | null | undefined;
  availableCredits: number | null | undefined;
  isCustomerLoading: boolean;
  isCustomerFetching: boolean;
  currentProduct: ISubscription | undefined;
  isSubscriptionsLoading: boolean;
  isProductsFetching: boolean;
  customerError: unknown;
  subscriptions: ISubscription[];
  recentlyUpdatedPlanName: string | null;
  updateSubscription: ({ subscription }: HandleSubscriptionType) => void;
  setRecentlyUpdatedPlanName: Dispatch<SetStateAction<string | null>>;
};

type HandleSubscriptionType = {
  subscription: ISubscription;
  callback?: (res: CommonResponse) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any) => void;
  onFinaly?: () => void;
};

const initialValue = {
  customer: null,
  activeSubscription: null,
  latestSubscription: null,
  remainingDaysUntilPayment: null,
  availableCredits: undefined,
  isCustomerLoading: true,
  isCustomerFetching: true,
  currentProduct: undefined,
  isSubscriptionsLoading: true,
  isProductsFetching: true,
  customerError: null,
  subscriptions: [],
  recentlyUpdatedPlanName: null,
  updateSubscription: unimplemented,
  setRecentlyUpdatedPlanName: unimplemented,
};

const CustomerContext = createContext<ContextValueType>(initialValue);

export const CustomerProvider = ({ children }: { children: ReactNode }) => {
  const [recentlyUpdatedPlanName, setRecentlyUpdatedPlanName] = useState<string | null>(null);

  const {
    data: subscriptions,
    isLoading: isSubscriptionsLoading,
    isProductsFetching,
  } = useSubscriptions();

  const queryClient = useQueryClient();
  const { user } = useUser();

  const {
    data: customer,
    isLoading: isCustomerLoading,
    error: customerError,
    isFetching: isCustomerFetching,
  } = useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY.CUSTOMER],
    queryFn: fetchCustomer,
    staleTime: 60 * 60 * 1000, // 1 Hour
    enabled: !!user,
    refetchInterval: recentlyUpdatedPlanName ? 2000 : false,
    onSuccess: ({ data }) => {
      if (!recentlyUpdatedPlanName || !data) return;

      const activeSubscription = data?.active_subscription;
      const latestSubscription = data?.subscriptions?.reduce((acc, current) => {
        return new Date(acc?.created_at).getTime() > new Date(current?.created_at).getTime()
          ? acc
          : current;
      }, data?.subscriptions[0] || null);

      if (activeSubscription?.name === latestSubscription?.name) {
        recentlyUpdatedPlanName && setRecentlyUpdatedPlanName(null);
      } else {
        queryClient.invalidateQueries([SUBSCRIPTION_QUERY_KEY.CUSTOMER]);
        queryClient.invalidateQueries([SUBSCRIPTION_QUERY_KEY.PRODUCTS]);
      }
    },
  });

  const activeSubscription = useMemo(() => customer?.data.active_subscription, [customer]) || null;

  const latestSubscription = useMemo(() => {
    if (!customer?.data?.subscriptions.length) {
      return null;
    }

    return customer?.data.subscriptions?.reduce((acc, current) => {
      return new Date(acc?.created_at).getTime() > new Date(current?.created_at).getTime()
        ? acc
        : current;
    });
  }, [customer]);

  const remainingDaysUntilPayment = useMemo<number | undefined>(
    () => getDifferenceInDays(activeSubscription?.ends_at),
    [activeSubscription]
  );

  const availableCredits = useMemo(() => customer?.data?.credits, [customer]);

  const currentProduct = useMemo(
    () =>
      subscriptions.find(
        (item) =>
          item.name.toLowerCase().replaceAll(' ', '_') === customer?.data?.active_subscription.name
      ),
    [subscriptions, customer]
  );

  const updateSubscription = async ({
    subscription,
    callback,
    onError,
    onFinaly,
  }: HandleSubscriptionType) => {
    if (!subscription?.billing_info?.id) return;

    updateSubscriptionService({
      method: subscription.method,
      price_id: subscription.billing_info.id,
    })
      .then((res) => callback && callback(res))
      .catch((error) => onError && onError(error))
      .finally(() => onFinaly && onFinaly());
  };

  const customerContextValue = useMemo(
    () => ({
      customer: customer?.data || null,
      isCustomerLoading,
      isCustomerFetching,
      activeSubscription,
      latestSubscription,
      remainingDaysUntilPayment,
      currentProduct,
      availableCredits,
      isSubscriptionsLoading,
      isProductsFetching,
      subscriptions,
      customerError,
      recentlyUpdatedPlanName,
      updateSubscription,
      setRecentlyUpdatedPlanName,
    }),
    [
      customer,
      isCustomerLoading,
      isCustomerFetching,
      activeSubscription,
      latestSubscription,
      remainingDaysUntilPayment,
      currentProduct,
      availableCredits,
      isSubscriptionsLoading,
      isProductsFetching,
      subscriptions,
      customerError,
      recentlyUpdatedPlanName,
      updateSubscription,
      setRecentlyUpdatedPlanName,
    ]
  );

  return (
    <CustomerContext.Provider value={customerContextValue}>{children}</CustomerContext.Provider>
  );
};

export const useCustomerContext = () => useContext(CustomerContext) as ContextValueType;
